import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Stack,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { supabase } from '../../config/supabase'
import type { ServicesTemplate, ServicesTemplateStructure, ServicesTemplateField } from '../../types'

export default function ServicesTemplateEditor() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [template, setTemplate] = useState<ServicesTemplate | null>(null)
  const [templateStructure, setTemplateStructure] = useState<ServicesTemplateStructure | null>(null)

  useEffect(() => {
    fetchTemplate()
  }, [])

  const fetchTemplate = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('services_templates')
        .select('*')
        .eq('name', 'Komplet service skabelon 2025')
        .limit(1)

      if (error) throw error

      if (data && data.length > 0) {
        setTemplate(data[0])
        setTemplateStructure(data[0].template as ServicesTemplateStructure)
      }
    } catch (err: any) {
      setError('Kunne ikke hente skabelon: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!template || !templateStructure) return

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const { error } = await supabase
        .from('services_templates')
        .update({
          name: template.name,
          description: template.description,
          template: templateStructure,
          updated_at: new Date().toISOString(),
        })
        .eq('id', template.id)

      if (error) throw error

      setSuccess('✅ Skabelon gemt og opdateret i databasen!')
      
      // Refresh to get latest data
      await fetchTemplate()
    } catch (err: any) {
      setError('Fejl ved gem: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const updateSection = (sectionIndex: number, field: string, value: any) => {
    if (!templateStructure) return
    
    const newStructure = { ...templateStructure }
    newStructure.sections[sectionIndex] = {
      ...newStructure.sections[sectionIndex],
      [field]: value
    }
    setTemplateStructure(newStructure)
  }

  const updateField = (sectionIndex: number, fieldIndex: number, field: string, value: any) => {
    if (!templateStructure) return
    
    const newStructure = { ...templateStructure }
    newStructure.sections[sectionIndex].fields[fieldIndex] = {
      ...newStructure.sections[sectionIndex].fields[fieldIndex],
      [field]: value
    }
    setTemplateStructure(newStructure)
  }

  const addField = (sectionIndex: number) => {
    if (!templateStructure) return
    
    const newField: ServicesTemplateField = {
      id: `field_${Date.now()}`,
      label: 'Nyt felt',
      type: 'text',
      placeholder: ''
    }
    
    const newStructure = { ...templateStructure }
    newStructure.sections[sectionIndex].fields.push(newField)
    setTemplateStructure(newStructure)
  }

  const removeField = (sectionIndex: number, fieldIndex: number) => {
    if (!templateStructure) return
    
    const newStructure = { ...templateStructure }
    newStructure.sections[sectionIndex].fields.splice(fieldIndex, 1)
    setTemplateStructure(newStructure)
  }

  const removeSection = (sectionIndex: number) => {
    if (!templateStructure) return
    
    const sectionTitle = templateStructure.sections[sectionIndex].title
    if (!window.confirm(`Er du sikker på at du vil slette sektionen "${sectionTitle}"? Dette kan ikke fortrydes.`)) {
      return
    }
    
    const newStructure = { ...templateStructure }
    newStructure.sections.splice(sectionIndex, 1)
    setTemplateStructure(newStructure)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!template || !templateStructure) {
    return (
      <Alert severity="error">
        Ingen skabelon fundet. Kør SQL migration først.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Service-skabelon Editor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Rediger alle felter, beskrivelser og labels i skabelonen
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Gemmer...' : 'Gem i Database'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Template Metadata */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Skabelon Information
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Skabelon Navn"
                value={template.name}
                disabled
                helperText="Navnet kan ikke ændres"
              />
              <TextField
                fullWidth
                label="Kategori"
                value={template.category || ''}
                onChange={(e) => setTemplate({ ...template, category: e.target.value })}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Beskrivelse"
              value={template.description || ''}
              onChange={(e) => setTemplate({ ...template, description: e.target.value })}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Sections */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Sektioner ({templateStructure.sections.length})
      </Typography>

      {templateStructure.sections.map((section, sectionIndex) => (
        <Accordion key={section.id} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
                <Chip 
                  label={`${section.fields.length} felter`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
              <IconButton 
                size="small" 
                color="error"
                onClick={(e) => {
                  e.stopPropagation()
                  removeSection(sectionIndex)
                }}
                title="Slet sektion"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Section Info */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Sektion Titel"
                value={section.title}
                onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Sektion Beskrivelse"
                value={section.description || ''}
                onChange={(e) => updateSection(sectionIndex, 'description', e.target.value)}
                placeholder="Valgfri beskrivelse der vises under titlen"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Fields */}
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Felter i denne sektion:
            </Typography>

            {section.fields.map((field, fieldIndex) => (
              <Paper key={field.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Felt #{fieldIndex + 1}
                  </Typography>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => removeField(sectionIndex, fieldIndex)}
                    title="Fjern felt"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Felt ID"
                      value={field.id}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'id', e.target.value)}
                      helperText="Unikt ID (brug kun a-z, 0-9, _)"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Label"
                      value={field.label}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'label', e.target.value)}
                      helperText="Synlig tekst til brugeren"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      select
                      SelectProps={{ native: true }}
                      label="Type"
                      value={field.type}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'type', e.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="number">Number</option>
                      <option value="price">Price (kr.)</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="select">Select</option>
                      <option value="email">Email</option>
                      <option value="tel">Telefon</option>
                      <option value="checkboxGroup">Checkbox Gruppe</option>
                      <option value="productLevels">Produkt Niveauer (kiste/urne)</option>
                      <option value="serviceList">Service Liste</option>
                      <option value="packagePrice">Pakke med inkluderet liste</option>
                    </TextField>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Placeholder"
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'placeholder', e.target.value)}
                      helperText="Eksempel-tekst i feltet"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Unit"
                      value={field.unit || ''}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'unit', e.target.value)}
                      helperText='F.eks. "kr." for pris'
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label="Forklaring"
                    value={field.explanation || ''}
                    onChange={(e) => updateField(sectionIndex, fieldIndex, 'explanation', e.target.value)}
                    helperText="Hjælpetekst der forklarer feltet"
                  />

                  {/* Options editor for checkboxGroup, productLevels, serviceList */}
                  {['checkboxGroup', 'productLevels', 'serviceList'].includes(field.type) && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        📋 Valgmuligheder (options)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Én pr. linje: id|label|beskrivelse (beskrivelse er valgfri)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={6}
                        value={
                          (field.options || [])
                            .map((opt: any) => 
                              opt.description 
                                ? `${opt.id}|${opt.label}|${opt.description}`
                                : `${opt.id}|${opt.label}`
                            )
                            .join('\n')
                        }
                        onChange={(e) => {
                          const lines = e.target.value.split('\n').filter(l => l.trim())
                          const options = lines.map(line => {
                            const [id, label, description] = line.split('|').map(s => s.trim())
                            return description 
                              ? { id: id || '', label: label || '', description }
                              : { id: id || '', label: label || '' }
                          })
                          updateField(sectionIndex, fieldIndex, 'options', options)
                        }}
                        placeholder="standard|Standard kiste|Den kiste der indgår i pakker&#10;mellem|Mellem kiste|Kiste med bedre finish"
                      />
                    </Box>
                  )}

                  {/* Included items editor for packagePrice */}
                  {field.type === 'packagePrice' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        ✅ Inkluderet i pakken
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Én ydelse pr. linje - vises som liste til brugeren
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={8}
                        value={((field as any).included || []).join('\n')}
                        onChange={(e) => {
                          const items = e.target.value.split('\n').filter(l => l.trim())
                          updateField(sectionIndex, fieldIndex, 'included', items)
                        }}
                        placeholder="Afhentning af afdøde&#10;Standard kiste&#10;Standard urne&#10;Ilægning og påklædning&#10;Kørsel (20 km inkluderet)"
                      />
                    </Box>
                  )}
                </Stack>
              </Paper>
            ))}

            {/* Add Field Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => addField(sectionIndex)}
              sx={{ mt: 2 }}
            >
              Tilføj Felt til {section.title}
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Save Button (Bottom) */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Gemmer til Database...' : 'Gem Ændringer'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          💡 Om skabelonen:
        </Typography>
        <Typography variant="body2">
          • Alle ændringer gemmes direkte i databasen<br />
          • Felter med samme ID i forskellige sektioner skal have unikke ID'er<br />
          • Skabelonen bruges når du sender links til bedemænd via "E-mail System"<br />
          • Bedemænd ser denne struktur når de udfylder formularen
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          📦 Felttyper:
        </Typography>
        <Typography variant="body2" component="div">
          <strong>checkboxGroup</strong> - Valgmuligheder med checkboxes (trosretninger, begravelsesformer)<br />
          <strong>productLevels</strong> - Produktniveauer med checkbox + pris (kister, urner)<br />
          <strong>serviceList</strong> - Liste af services med checkbox + pris (ekstra ydelser)<br />
          <strong>packagePrice</strong> - Pakke med liste over hvad der er inkluderet + samlet pris
        </Typography>
      </Alert>
    </Box>
  )
}

